import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ExtendedCard,
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  SettingsButton,
} from "@NP-Webber/crm-kit";
import {
  platformApi,
  type PlatformModuleCatalogItem,
  type PlatformTenant,
  type TenantModuleView,
} from "../../api/platformApi";
import { toClientApiError } from "../../api/httpClient";
import {
  AccountBalanceWalletOutlinedIcon,
  BlockOutlinedIcon,
  MenuBookOutlinedIcon,
} from "../../ui/appIcons";
import { formatOrganizationCode, formatOrganizationSubtitle } from "./orgDisplay";
import { PlatformMailingPanel } from "../../next/mailing/PlatformMailingPanel";
import { PlatformAiWalletPanel } from "../../next/ai/credits/PlatformAiWalletPanel";

export type OrganizationCardProps = {
  open: boolean;
  organization: PlatformTenant | null;
  onClose: () => void;
  onOpenGuidance?: (organization: PlatformTenant) => void;
  onRequestDisable?: (organization: PlatformTenant) => void;
};

export function OrganizationCard({
  open,
  organization,
  onClose,
  onOpenGuidance,
  onRequestDisable,
}: OrganizationCardProps) {
  const [modules, setModules] = useState<TenantModuleView[]>([]);
  const [catalog, setCatalog] = useState<PlatformModuleCatalogItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editModule, setEditModule] = useState<TenantModuleView | null>(null);
  const [editStatus, setEditStatus] = useState<"active" | "disabled">("active");
  const [editFeatures, setEditFeatures] = useState<string[]>([]);

  const loadModules = useCallback(async () => {
    if (!organization) {
      return;
    }
    setError(null);
    try {
      const [snap, cat] = await Promise.all([
        platformApi.listTenantModules(organization.id),
        platformApi.listModuleCatalog(),
      ]);
      setModules(snap.modules);
      setCatalog(cat);
    } catch (err) {
      setError(toClientApiError(err).message);
    }
  }, [organization]);

  useEffect(() => {
    if (open && organization) {
      void loadModules();
    }
  }, [open, organization, loadModules]);

  const detailsFields = useMemo(
    () => [
      {
        key: "code",
        label: "קוד ארגון",
        value:
          organization?.code != null ? formatOrganizationCode(organization.code) : "",
      },
      { key: "name", label: "שם הארגון", value: organization?.name ?? "" },
      { key: "slug", label: "מזהה (slug)", value: organization?.slug ?? "" },
      { key: "id", label: "מזהה פנימי", value: organization?.id ?? "" },
    ],
    [organization],
  );

  if (!open || !organization) {
    return null;
  }

  const catalogByKey = new Map(catalog.map((c) => [c.key, c]));

  return (
    <>
      <ExtendedCard
        key={organization.id}
        open
        onClose={onClose}
        title={organization.name}
        subtitle={formatOrganizationSubtitle(organization.code, organization.slug)}
        detailsFields={detailsFields}
        feedSources={{ activities: [], tasks: [], notes: [] }}
        hideFeedTab
        showDashboard={false}
        headerActions={(() => {
          const actions: Array<{
            tooltip: string;
            onClick: () => void;
            icon?: ReactNode;
          }> = [];
          if (onOpenGuidance) {
            actions.push({
              tooltip: "מסמך הנחיות",
              icon: <MenuBookOutlinedIcon />,
              onClick: () => onOpenGuidance(organization),
            });
          }
          if (onRequestDisable) {
            actions.push({
              tooltip: "כיבוי ארגון",
              icon: <BlockOutlinedIcon />,
              onClick: () => onRequestDisable(organization),
            });
          }
          return actions.length > 0 ? actions : undefined;
        })()}
        detailsHeaderExtra={
          onOpenGuidance ? (
            <SettingsButton
              type="button"
              variant="primarySettings"
              size="sm"
              onClick={() => onOpenGuidance(organization)}
            >
              מסמך הנחיות
            </SettingsButton>
          ) : null
        }
        extraTabs={[
          {
            label: "מודולים",
            icon: <AccountBalanceWalletOutlinedIcon />,
            content: (
              <div className="related-panel">
                {error ? <FormAlert severity="error">{error}</FormAlert> : null}
                <FormAlert severity="info">
                  הפעלה וכיבוי של מודולים לארגון (ADR-0015). פיצ׳רי כספים שולטים
                  בהכנסות/הוצאות ותתי־הלשוניות.
                </FormAlert>
                <ul className="related-panel__list">
                  {modules.map((mod) => (
                    <li key={mod.key} className="related-panel__item">
                      <strong>
                        {mod.displayNameHe}{" "}
                        <span>({mod.status === "active" ? "פעיל" : "כבוי"})</span>
                      </strong>
                      <span>
                        {mod.features.length > 0
                          ? mod.features.join(", ")
                          : "ללא פיצ׳רים"}
                      </span>
                      <SettingsButton
                        type="button"
                        size="sm"
                        onClick={() => {
                          setEditModule(mod);
                          setEditStatus(mod.status);
                          setEditFeatures([...mod.features]);
                          setEditOpen(true);
                        }}
                      >
                        עריכה
                      </SettingsButton>
                    </li>
                  ))}
                </ul>
                {modules.length === 0 ? (
                  <p className="related-panel__empty">אין מודולים רשומים.</p>
                ) : null}
              </div>
            ),
          },
          {
            label: "דיוור",
            icon: <MenuBookOutlinedIcon />,
            content: <PlatformMailingPanel tenantId={organization.id} />,
          },
          {
            label: "AI",
            icon: <AccountBalanceWalletOutlinedIcon />,
            content: <PlatformAiWalletPanel tenantId={organization.id} />,
          },
          {
            label: "תשלומים",
            icon: <AccountBalanceWalletOutlinedIcon />,
            content: (
              <div className="related-panel">
                <FormAlert severity="info">
                  מעקב תשלומים, מנוי וחשבוניות לארגון זה יתווספו בהמשך.
                </FormAlert>
                <p className="related-panel__empty">אין נתוני תשלום עדיין.</p>
              </div>
            ),
          },
        ]}
      />

      <FormDialog
        open={editOpen && Boolean(editModule)}
        onClose={() => {
          if (!busy) {
            setEditOpen(false);
          }
        }}
        size="md"
      >
        {editModule ? (
          <FormCard
            title={`מודול: ${editModule.displayNameHe}`}
            error={error}
            onSave={() => {
              void (async () => {
                setBusy(true);
                setError(null);
                try {
                  await platformApi.setTenantModule(organization.id, {
                    moduleKey: editModule.key,
                    status: editStatus,
                    features: editFeatures,
                  });
                  setEditOpen(false);
                  await loadModules();
                } catch (err) {
                  setError(toClientApiError(err).message);
                } finally {
                  setBusy(false);
                }
              })();
            }}
            onCancel={() => setEditOpen(false)}
            saveLabel="שמירה"
            cancelLabel="ביטול"
            saving={busy}
          >
            <FormGrid>
              <FormField
                label="סטטוס"
                value={editStatus}
                onChange={(v) =>
                  setEditStatus(v as "active" | "disabled")
                }
                options={[
                  { value: "active", label: "פעיל" },
                  { value: "disabled", label: "כבוי" },
                ]}
              />
              {(catalogByKey.get(editModule.key)?.features ?? []).map((feature) => (
                <label key={feature} style={{ display: "flex", gap: "0.5rem" }}>
                  <input
                    type="checkbox"
                    checked={editFeatures.includes(feature)}
                    onChange={(e) => {
                      setEditFeatures((prev) =>
                        e.target.checked
                          ? [...prev, feature]
                          : prev.filter((f) => f !== feature),
                      );
                    }}
                  />
                  {feature}
                </label>
              ))}
            </FormGrid>
          </FormCard>
        ) : null}
      </FormDialog>
    </>
  );
}
